// Pure string helpers for answer normalisation and agreement handling.
// No DOM, no localStorage — safe to unit-test.

import type { Card, Direction } from "../types";

export function stripDiacritics(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function normalizeHeader(s: string): string {
  return stripDiacritics((s || "").toLowerCase().trim());
}

// Normalise a user answer for comparison: lowercase, strip parenthetical
// hints, collapse whitespace, trim trailing punctuation.
export function normalizeAnswer(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[.!?;:,]+$/g, "")
    .trim();
}

// Split a field on "/" into synonym variants.
export function splitVariants(s: string): string[] {
  return (s || "")
    .split("/")
    .map((v) => v.trim())
    .filter(Boolean);
}

export type AgreementInfo = {
  base: string;
  agreements: string[];
  synonyms: string[];
  hasAgreements: boolean;
};

/**
 * Split agreement forms from synonyms.
 * Agreement forms are separated by ";", synonyms by "/".
 *   base = first form (masculine for adjectives)
 *   agreements = all forms including base (split on ";")
 *   synonyms = alternative words (split on "/") within the base form
 */
export function splitAgreements(text: string): AgreementInfo {
  const parts = (text || "")
    .split(";")
    .map((v) => v.trim())
    .filter(Boolean);
  const base = parts[0] || text || "";
  // The base itself may contain "/" for synonyms.
  const synonyms = base
    .split("/")
    .map((v) => v.trim())
    .filter(Boolean);
  return {
    base: synonyms[0] || base,
    agreements: parts,
    synonyms,
    hasAgreements: parts.length > 1,
  };
}

// Labels for agreement forms (m, f, n, pl).
export const AGREEMENT_LABELS: Record<number, string> = {
  0: "m.",
  1: "f.",
  2: "n.",
  3: "pl.",
};

// Variants used for matching: parenthesised hints are stripped FIRST so
// a '/' inside parens (e.g. "langue (organe / parler)") is treated as
// part of the hint, not as a variant separator.
export function matchableVariants(expected: string): string[] {
  const stripped = (expected || "").replace(/\([^)]*\)/g, " ");
  return splitVariants(stripped);
}

export function answerMatches(user: string, expected: string): boolean {
  const u = normalizeAnswer(user);
  if (!u) return false;
  return matchableVariants(expected).some((v) => normalizeAnswer(v) === u);
}

export function displayAnswer(expected: string): string {
  return splitVariants(expected).join(" / ");
}

/**
 * Prompt text to display on the card. For words with agreement forms
 * (";"-separated), show only the base form when prompting from that
 * language so learners see a clean lemma.
 */
export function getPromptText(card: Card, dir: Direction): string {
  const raw = card.faces[dir.from] || "";
  const info = splitAgreements(raw);
  return info.hasAgreements ? info.base : raw;
}

/**
 * Expected answer text used for validation. Same rule as getPromptText
 * but on the target side — accept only the base (masculine) form.
 */
export function getExpectedText(card: Card, dir: Direction): string {
  const raw = card.faces[dir.to] || "";
  const info = splitAgreements(raw);
  return info.hasAgreements ? info.base : raw;
}
